/**
 * Working Patterns API Client
 * Handles API calls for organization and employee working patterns
 */

import { apiClient } from '@/services/apiClient';
import type { WorkingPatternDto, EmployeeWorkingPatternDto } from '@/types/api';
import { 
  fromWorkingPatternDto,
  fromEmployeeWorkingPatternDto,
  toCreateWorkingPatternDto 
} from './mappers';
import type { WorkingPattern } from '@/types/timesheet';


/**
 * Get current organization working pattern from API
 * @returns Current organization working pattern
 */
export async function getOrganizationPattern(): Promise<WorkingPattern> {
  const dto = await apiClient.get<WorkingPatternDto>('/working-patterns/organization/current');
  return fromWorkingPatternDto(dto);
}

/**
 * Set organization working pattern via API
 * @param pattern - The working pattern to set
 * @returns The created working pattern
 */
export async function setOrganizationPattern(pattern: WorkingPattern): Promise<WorkingPattern> {
  const dto = toCreateWorkingPatternDto(pattern);
  const createdDto = await apiClient.put<WorkingPatternDto>('/working-patterns/organization', dto);
  return fromWorkingPatternDto(createdDto);
}

/**
 * Get organization working pattern history from API
 * @param limit - Maximum number of historical records to retrieve
 * @returns Array of historical working patterns
 */
export async function getOrganizationPatternHistory(limit: number = 50): Promise<WorkingPattern[]> {
  const dtos = await apiClient.get<WorkingPatternDto[]>(`/working-patterns/organization/history?limit=${limit}`);
  return dtos.map(fromWorkingPatternDto);
}


/**
 * Get current working pattern for an employee from API
 * @param employeeId - The employee ID
 * @returns Employee working pattern with metadata (inherited flag, pattern ID)
 */
export async function getEmployeePattern(employeeId: string): Promise<{
  pattern: WorkingPattern;
  inherited: boolean;
  id: string;
}> {
  const dto = await apiClient.get<EmployeeWorkingPatternDto>(
    `/employees/${employeeId}/working-patterns/current`
  );
  return fromEmployeeWorkingPatternDto(dto);
}

/**
 * Set custom working pattern for an employee via API
 * @param employeeId - The employee ID
 * @param pattern - The working pattern to set
 * @returns The created employee working pattern with metadata
 */
export async function setEmployeePattern(
  employeeId: string, 
  pattern: WorkingPattern
): Promise<{
  pattern: WorkingPattern;
  inherited: boolean;
  id: string;
}> {
  const dto = toCreateWorkingPatternDto(pattern);
  const createdDto = await apiClient.post<EmployeeWorkingPatternDto>(
    `/employees/${employeeId}/working-patterns`,
    dto
  );
  return fromEmployeeWorkingPatternDto(createdDto);
}

/**
 * Delete custom working pattern for an employee (revert to organization default)
 * @param employeeId - The employee ID
 * @param patternId - The pattern ID to delete
 */
export async function deleteEmployeePattern(
  employeeId: string,
  patternId: string
): Promise<void> {
  await apiClient.delete(`/employees/${employeeId}/working-patterns/${patternId}`);
}

/**
 * Get working pattern history for an employee from API
 * @param employeeId - The employee ID
 * @param limit - Maximum number of historical records to retrieve
 * @returns Array of historical employee working patterns
 */
export async function getEmployeePatternHistory(
  employeeId: string,
  limit: number = 50
): Promise<Array<{
  pattern: WorkingPattern;
  inherited: boolean;
  id: string;
}>> {
  const dtos = await apiClient.get<EmployeeWorkingPatternDto[]>(
    `/employees/${employeeId}/working-patterns/history?limit=${limit}`
  );
  return dtos.map(fromEmployeeWorkingPatternDto);
}
